import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "components/Loader";
import ArticleHeadlinesKeywordTable from "components/Tables/ArticleHeadlinesKeywordTable";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import { get_seo_article_headline_for_single_keyword } from "services/buddyXApiHubService";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";

export default function Page() {
  const navigate = useNavigate();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [brandName, setBrandName] = useState("");
  const [formData, setFormData] = useState({
    keywords: [],
  });
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [errors, setErrors] = useState({ keyword: "", brandName: "" });
  const [tabs, setTabs] = useState([]);
  
  const firstLevelTopic = "Content Editor";
  const secoundLevelTopic = "Headlines  Generation";
  const thirdLevelTopic = "Headline Creation";

  // Roadmap steps data
  const roadmapSteps = [
    {
      id: 1,
      title: "Headline",
      subtitle: "Create compelling headlines",
      isCompleted: isResultGot, // Completed when headlines are generated
      isActive: true, // Always clickable
      link: "/content_editor/headlines/keyword",
      description: "Generate engaging headlines that capture attention and improve click-through rates."
    },
    {
      id: 2,
      title: "Structure",
      subtitle: "Build content outline",
      isCompleted: false,
      isActive: true, // Always clickable
      link: "/content_editor/writer/keyword",
      description: "Create a well-structured content outline based on your target keywords."
    },
    {
      id: 3,
      title: "Create",
      subtitle: "Write content draft",
      isCompleted: false,
      isActive: true, // Always clickable
      link: "/content_editor/writer/keyword",
      description: "Generate high-quality content drafts using your outline and keywords."
    },
    {
      id: 4,
      title: "Optimize",
      subtitle: "AI overview analysis",
      isCompleted: false,
      isActive: true, // Always clickable
      link: "/content_editor/ai_overview/content",
      description: "Get AI-powered insights and optimization suggestions for your content."
    },
    {
      id: 5,
      title: "Publish",
      subtitle: "Go live with confidence",
      isCompleted: false,
      isActive: true, // Always clickable
      link: "#",
      description: "Your content is ready to publish and drive results!"
    }
  ];

  // Styling functions for roadmap steps
  const getStepStyles = (step) => {
    if (step.isCompleted) {
      return {
        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
        color: 'white',
        border: '2px solid #28a745',
        cursor: 'pointer'
      };
    } else if (step.isActive) {
      return {
        background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
        color: 'white',
        transform: 'scale(1.05)',
        boxShadow: '0 8px 25px rgba(74, 144, 226, 0.3)',
        cursor: 'pointer'
      };
    } else {
      return {
        background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
        color: 'white',
        cursor: 'pointer'
      };
    }
  };

  const getConnectorStyles = (index) => {
    if (index < roadmapSteps.length - 1) {
      const currentStep = roadmapSteps[index];
      
      if (currentStep.isCompleted) {
        return {
          background: 'linear-gradient(90deg, #28a745 0%, #20c997 100%)',
          height: '4px'
        };
      } else {
        return {
          background: 'linear-gradient(90deg, #4A90E2 0%, #6c757d 100%)',
          height: '3px'
        };
      }
    }
    return {};
  };

  // Function to open links in new tab with proper authentication handling
  const handleNavigation = (url) => {
    // Create a proper link element for new tab that preserves authentication
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleBrandNameChange = (e) => {
    setBrandName(e.target.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "keyword" &&
      !Validator.validateRequired(value, Validator.validateSingleKeyword)
    ) {
      error = "Please enter keywords";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setIsResultGot(false);
    setKeyword("");
    setBrandName("");
    setShowErrorNotification(false);
    setErrorMessage("");
    setErrors({ keyword: "", brandName: "" });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await get_seo_article_headline_for_single_keyword(formData, handleInsufficientCredits);
      setShowSearchItem(formData.keywords[0]);
      setTabs(
        Object.keys(result).map((key, index) => ({
          label: `Draft ${String.fromCharCode(65 + index)}`,
          element: (
            <ArticleHeadlinesKeywordTable
              data={result[key]}
              topic={thirdLevelTopic}
            />
          ),
        }))
      );
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(error.message || "Failed to generate article headlines. Please try again.");
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  const handleInsufficientCredits = (balanceData) => {
    
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  useEffect(() => {
    let isValid = Validator.validateRequired(keyword, Validator.validateSingleKeyword) &&
                  Validator.validateOptional(brandName, Validator.validateBrandName);
    if (isValid) {
      setFormData({
        keywords: [keyword],
        brand_name: brandName,
      });
    }
    setIsFormValid(isValid);
  }, [keyword, brandName]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Find the most compelling <b>article headlines</b> tailored to your content,
        ensuring maximum click-through rates and engagement. Generate eye-catching
        titles that resonate with your audience and drive traffic to your site.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Article Headlines helps you craft compelling, optimized titles for your articles, boosting engagement and driving more traffic by improving click-through rates and search visibility."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Find Your Headlines</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter a keyword and brand name to generate article headlines.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.keyword}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="brandName"
                    placeholder="Enter Brand Name"
                    value={brandName}
                    onChange={handleBrandNameChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.brandName}
                    className="px-2"
                    style={getInputFieldStyle('type6')}
                  />
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type6', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type6', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              Headlines for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <MobileSupportTabBar tabs={tabs} />}
      </div>

      {/* Visual Roadmap Section */}
      {isResultGot && <div className="container-fluid px-0 mt-5">
        <div className="row g-0">
          <div className="col-12">
            <div className="p-2 p-md-3 mb-3 rounded" style={{ maxWidth: '100%' }}>
              <div className="d-flex fw-bold mb-2 mb-md-3 h5 h-md-4">SEO Content Creation Roadmap</div>
              <div className="mb-3">
                <p className="fw-normal justify-content-start align-items-center">
                  Follow these steps to create optimized content. Each step opens in a new tab and unlocks the next step.
                </p>

                {/* Visual Roadmap Representation */}
                <div className="mb-4 p-2 p-sm-3 p-md-4 rounded" style={{ backgroundColor: '#f8f9fa', border: '2px solid #20c997', maxWidth: '100%', overflowX: 'auto' }}>
                  <div className="text-center mb-3 mb-md-4">
                    <h4 className="fw-bold h6 h-sm-5 h-md-4 mb-2 mb-md-3" style={{ color: '#4A90E2' }}>
                      What is Next? SEO Content Creation Roadmap
                    </h4>
                    <div className="d-inline-flex align-items-center px-2 px-sm-3 py-1 py-sm-2 rounded-pill" 
                         style={{ 
                           background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
                           color: 'white',
                           fontSize: '0.8rem'
                         }}>
                      <span className="me-1 me-sm-2" style={{ fontSize: '0.9rem' }}>🎯</span>
                      <span className="fw-semibold">Your Journey to Perfect Content</span>
                    </div>
                  </div>
                  
                  {/* Steps Container - Desktop/Large Screen */}
                  <div className="d-none d-md-flex justify-content-between align-items-center position-relative mb-3" style={{ minWidth: 'fit-content' }}>
                    {roadmapSteps.map((step, index) => (
                      <React.Fragment key={step.id}>
                        {/* Step Circle */}
                        <div className="text-center position-relative" style={{ zIndex: 2, flex: '0 0 auto' }}>
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 mb-lg-3 transition-all"
                            style={{
                              width: '70px',
                              height: '70px',
                              fontSize: '1.3rem',
                              fontWeight: 'bold',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              ...getStepStyles(step)
                            }}
                            onClick={() => step.link !== '#' && handleNavigation(step.link)}
                          >
                            {step.isCompleted ? '✓' : step.id}
                          </div>
                          <div style={{ maxWidth: '100px', minWidth: '80px' }}>
                            <h6 className="fw-bold mb-1" style={{ 
                              color: '#2c3e50',
                              fontSize: '0.9rem'
                            }}>
                              {step.title}
                            </h6>
                            <p className="small text-muted mb-0" style={{ 
                              fontSize: '0.75rem',
                              lineHeight: '1.2'
                            }}>
                              {step.subtitle}
                            </p>
                          </div>
                        </div>

                        {/* Connector Line */}
                        {index < roadmapSteps.length - 1 && (
                          <div 
                            className="flex-grow-1 mx-2"
                            style={{
                              ...getConnectorStyles(index),
                              borderRadius: '2px',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              top: '-35px'
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Mobile/Tablet Steps - Vertical Layout */}
                  <div className="d-md-none">
                    {roadmapSteps.map((step, index) => (
                      <div key={step.id} className="position-relative">
                        <div className="d-flex align-items-start mb-3 mb-sm-4">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{
                              width: '50px',
                              height: '50px',
                              fontSize: '1.1rem',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              ...getStepStyles(step)
                            }}
                            onClick={() => step.link !== '#' && handleNavigation(step.link)}
                          >
                            {step.isCompleted ? '✓' : step.id}
                          </div>
                          <div className="flex-grow-1 pt-1">
                            <h6 className="fw-bold mb-1" style={{ 
                              color: '#2c3e50',
                              fontSize: '0.9rem'
                            }}>
                              {step.title}
                            </h6>
                            <p className="text-muted mb-0" style={{ 
                              fontSize: '0.75rem',
                              lineHeight: '1.3'
                            }}>
                              {step.subtitle}
                            </p>
                            {step.description && (
                              <p className="text-muted mb-0 mt-1 d-none d-sm-block" style={{ 
                                fontSize: '0.7rem',
                                lineHeight: '1.2'
                              }}>
                                {step.description}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Vertical Connector Line */}
                        {index < roadmapSteps.length - 1 && (
                          <div 
                            className="position-absolute"
                            style={{
                              left: '25px',
                              top: '50px',
                              width: '2px',
                              height: '25px',
                              ...getConnectorStyles(index)
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>}

      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
